import React, { useEffect, useState, useCallback } from "react";
import { Loader2, RefreshCw, Save, RotateCcw, ShieldCheck, ShieldX, Terminal } from "lucide-react";
import { toast } from "sonner";

import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isAdminRole } from "@/data/v12_user_nav";

const CATEGORY_ORDER = [
    { key: "feature_flags", label: "Feature Flags" },
    { key: "compliance", label: "Compliance" },
    { key: "misc", label: "Misc" },
];

function SettingCard({ item, onSave }) {
    const [value, setValue] = useState(item.value);
    const [saving, setSaving] = useState(false);
    useEffect(() => setValue(item.value), [item.value]);

    const dirty = JSON.stringify(value) !== JSON.stringify(item.value);
    const risky = item.risk === "high";

    const save = async () => {
        if (risky && !window.confirm(`This setting is HIGH-RISK. Are you sure?`)) return;
        setSaving(true);
        try {
            await onSave(item.key, value);
        } finally { setSaving(false); }
    };

    const reset = async () => {
        if (!window.confirm("Reset to default?")) return;
        setSaving(true);
        try {
            const res = await apiFetch(`/admin/system-settings/reset-default/${item.key}`, { method: "POST" });
            toast.success(`Reset ${item.key} → ${JSON.stringify(res.new_value)}`);
        } catch (e) { toast.error(e.message); }
        finally { setSaving(false); }
    };

    return (
        <div
            data-testid={`setting-${item.key}`}
            className={`rounded-2xl border p-4 ${risky ? "border-neutral-400/30 bg-neutral-400/[0.03]" : "border-[var(--color-border)] bg-[var(--color-card)]"}`}
        >
            <div className="flex items-center gap-2 mb-2">
                <code className="text-xs font-mono text-text-main">{item.key}</code>
                {risky && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded bg-neutral-400/10 text-neutral-400 border border-neutral-400/30">high risk</span>}
            </div>
            {item.description && <p className="text-xs text-text-secondary mb-3">{item.description}</p>}

            <div className="flex items-center gap-3 mb-2">
                {item.type === "bool" && (
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={!!value} onChange={(e) => setValue(e.target.checked)} data-testid={`setting-input-${item.key}`} />
                        <span className="text-xs">{value ? "Enabled" : "Disabled"}</span>
                    </label>
                )}
                {item.type === "int" && (
                    <input
                        data-testid={`setting-input-${item.key}`}
                        type="number"
                        value={value ?? 0}
                        onChange={(e) => setValue(parseInt(e.target.value, 10))}
                        min={item.min_value ?? undefined}
                        max={item.max_value ?? undefined}
                        className="w-32 rounded-lg bg-[var(--color-bg)] border border-[var(--color-border)] px-3 py-1.5 text-sm text-text-main"
                    />
                )}
                {item.type === "string" && (
                    <input
                        data-testid={`setting-input-${item.key}`}
                        type="text"
                        value={value ?? ""}
                        onChange={(e) => setValue(e.target.value)}
                        className="flex-1 rounded-lg bg-[var(--color-bg)] border border-[var(--color-border)] px-3 py-1.5 text-sm text-text-main"
                    />
                )}
            </div>

            <div className="flex items-center justify-between mt-3">
                <span className="text-[10px] text-text-secondary">
                    {item.updated_at && `Updated ${item.updated_at.slice(0,19).replace("T"," ")} by ${item.updated_by || "system"}`}
                </span>
                <div className="flex items-center gap-2">
                    <button onClick={reset} disabled={saving} data-testid={`setting-reset-${item.key}`} className="text-xs text-text-secondary hover:text-text-main inline-flex items-center gap-1"><RotateCcw className="h-3 w-3" /> Reset</button>
                    <button
                        onClick={save}
                        disabled={!dirty || saving}
                        data-testid={`setting-save-${item.key}`}
                        className="text-xs rounded-full bg-white hover:bg-[#EDEDED] text-black px-3 py-1 disabled:opacity-40 inline-flex items-center gap-1"
                    >
                        {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                        Save
                    </button>
                </div>
            </div>
        </div>
    );
}

function ApiKeyTab() {
    const [keys, setKeys] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        (async () => {
            try { const r = await apiFetch("/admin/system/api-keys"); setKeys(r.api_keys || []); }
            catch (e) { toast.error(e.message); }
            finally { setLoading(false); }
        })();
    }, []);
    if (loading) return <div className="py-8 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-[var(--color-primary)]" /></div>;
    return (
        <div className="space-y-2" data-testid="api-keys-tab">
            <p className="text-xs text-text-secondary mb-3">API keys are configured via env vars only, never editable in UI (security). Statuses reflect the current process env.</p>
            {keys.map((k) => (
                <div key={k.name} className="flex items-center justify-between rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] px-4 py-2" data-testid={`api-key-${k.name}`}>
                    <div>
                        <div className="text-sm font-medium">{k.name}</div>
                        <code className="text-[10px] text-text-secondary">{k.env_name}</code>
                    </div>
                    <div className="flex items-center gap-2">
                        {k.status === "OK" ? (
                            <span className="inline-flex items-center gap-1 text-xs text-[var(--color-success)]"><ShieldCheck className="h-3 w-3" /> OK</span>
                        ) : k.status === "Missing" ? (
                            <span className="inline-flex items-center gap-1 text-xs text-text-secondary"><ShieldX className="h-3 w-3" /> Missing</span>
                        ) : (
                            <span className="inline-flex items-center gap-1 text-xs text-[var(--color-alert)]"><ShieldX className="h-3 w-3" /> Invalid</span>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
}

function StripeTab() {
    const [data, setData] = useState(null);
    useEffect(() => { (async () => { try { setData(await apiFetch("/admin/system/stripe-webhook")); } catch (e) { toast.error(e.message); } })(); }, []);
    if (!data) return <div className="py-8 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-[var(--color-primary)]" /></div>;
    return (
        <div className="space-y-3" data-testid="stripe-tab">
            <div className="grid gap-3 sm:grid-cols-3">
                <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-3">
                    <div className="text-[10px] uppercase text-text-secondary">Webhook secret</div>
                    <div className={data.webhook_secret_configured ? "text-[var(--color-success)]" : "text-[var(--color-alert)]"}>
                        {data.webhook_secret_configured ? "Configured" : "Missing"}
                    </div>
                </div>
                <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-3">
                    <div className="text-[10px] uppercase text-text-secondary">Last event</div>
                    <div className="text-sm text-text-main">{data.last_event_type || "—"}</div>
                    <div className="text-[10px] text-text-secondary">{(data.last_event_received_at || "").slice(0, 19).replace("T", " ")}</div>
                </div>
                <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-3">
                    <div className="text-[10px] uppercase text-text-secondary">Verification rate</div>
                    <div className="text-sm">{data.signature_verification_ok_rate === null ? "—" : `${(data.signature_verification_ok_rate * 100).toFixed(1)}%`}</div>
                </div>
            </div>
            <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4">
                <h3 className="text-sm font-semibold mb-2">Recent events</h3>
                {data.recent_events?.length === 0 ? <p className="text-xs text-text-secondary">No events yet.</p> : (
                    <ul className="space-y-1 text-xs">
                        {data.recent_events.map((e, i) => (
                            <li key={i} className="flex items-center justify-between">
                                <span className="tabular-nums text-text-secondary">{(e.received_at || "").slice(0, 19).replace("T", " ")}</span>
                                <span className="text-text-main">{e.event_type}</span>
                                <span className={e.verified ? "text-[var(--color-success)]" : "text-[var(--color-alert)]"}>
                                    {e.verified ? "verified" : "invalid"}
                                </span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

function LogsTab() {
    const [lines, setLines] = useState([]);
    const [severity, setSeverity] = useState("");
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const p = new URLSearchParams();
            p.set("limit", "200");
            if (severity) p.set("severity", severity);
            if (q) p.set("q", q);
            const r = await apiFetch(`/admin/system/logs?${p}`);
            setLines(r.items || []);
        } catch (e) { toast.error(e.message); }
        finally { setLoading(false); }
    }, [severity, q]);

    useEffect(() => { load(); }, [load]);

    return (
        <div data-testid="logs-tab">
            <div className="flex items-center gap-2 mb-3">
                <select value={severity} onChange={(e) => setSeverity(e.target.value)} className="text-xs rounded-lg bg-[var(--color-card)] border border-[var(--color-border)] px-2 py-1 text-text-main">
                    <option value="">Any severity</option>
                    <option value="INFO">INFO</option>
                    <option value="WARNING">WARNING</option>
                    <option value="ERROR">ERROR</option>
                </select>
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Text filter…" className="text-xs rounded-lg bg-[var(--color-card)] border border-[var(--color-border)] px-2 py-1 text-text-main flex-1" />
                <button onClick={load} className="text-xs rounded-full border border-[var(--color-border)] text-text-secondary hover:text-text-main px-3 py-1 flex items-center gap-1"><RefreshCw className="h-3 w-3" /> Refresh</button>
            </div>
            <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] overflow-hidden max-h-[500px] overflow-y-auto">
                {loading ? <div className="p-8 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-[var(--color-primary)]" /></div> :
                lines.length === 0 ? <p className="p-4 text-xs text-text-secondary">No log lines match your filter.</p> :
                (<pre className="text-[10px] font-mono p-3 whitespace-pre-wrap">
                    {lines.map((l, i) => (
                        <div key={i} className={l.level === "ERROR" ? "text-[var(--color-alert)]" : l.level === "WARNING" ? "text-neutral-400" : "text-text-secondary"}>
                            <span className="text-text-secondary">{l.timestamp}</span> <span className="text-text-main">{l.level}</span> {l.message}
                        </div>
                    ))}
                </pre>)}
            </div>
        </div>
    );
}

const TABS = [
    { key: "feature_flags", label: "Feature Flags" },
    { key: "compliance", label: "Compliance" },
    { key: "api_keys", label: "API Keys" },
    { key: "stripe", label: "Stripe" },
    { key: "logs", label: "Logs" },
];

export default function AdminSystem() {
    const { user: me } = useAuth();
    const [settings, setSettings] = useState({ items: [], grouped: {} });
    const [tab, setTab] = useState("feature_flags");
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await apiFetch("/admin/system-settings");
            setSettings(r);
        } catch (e) { toast.error(e.message); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const save = async (key, value) => {
        try {
            await apiFetch(`/admin/system-settings/${key}`, { method: "PATCH", body: JSON.stringify({ value }) });
            toast.success(`${key} updated.`);
            await load();
        } catch (e) { toast.error(e.message); throw e; }
    };

    if (!isAdminRole(me?.role)) return <div className="min-h-screen bg-[var(--color-bg)] text-text-main flex items-center justify-center"><h1 className="text-2xl">Admin only</h1></div>;

    const currentItems = (settings.grouped || {})[tab] || [];

    return (
        <div className="min-h-screen bg-[var(--color-bg)] text-text-main flex">
            <Sidebar />
            <div className="flex-1 flex flex-col min-w-0">
                <Header />
                <main className="flex-1 p-6 md:p-8 max-w-5xl mx-auto w-full" data-testid="system-settings">
                    <div className="mb-6">
                        <div className="text-[10px] uppercase tracking-widest text-[var(--color-secondary)] mb-1">System panel</div>
                        <h1 className="text-3xl md:text-4xl font-semibold">Settings · {settings.items.length}</h1>
                    </div>

                    <div className="flex items-center gap-2 mb-6 overflow-x-auto no-scrollbar">
                        {TABS.map((t) => (
                            <button
                                key={t.key}
                                data-testid={`system-tab-${t.key}`}
                                onClick={() => setTab(t.key)}
                                className={`text-xs uppercase tracking-widest px-4 py-2 rounded-full shrink-0 ${tab === t.key ? "bg-white hover:bg-[#EDEDED] text-black" : "border border-[var(--color-border)] text-text-secondary hover:text-text-main"}`}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>

                    {loading ? (
                        <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-[var(--color-primary)]" /></div>
                    ) : tab === "api_keys" ? <ApiKeyTab /> :
                        tab === "stripe" ? <StripeTab /> :
                        tab === "logs" ? <LogsTab /> :
                        (
                            <div className="grid gap-3 md:grid-cols-2">
                                {currentItems.length === 0 ? <p className="text-xs text-text-secondary">No settings in this category yet.</p> :
                                    currentItems.map((it) => <SettingCard key={it.key} item={it} onSave={save} />)}
                            </div>
                        )}
                </main>
            </div>
        </div>
    );
}
